// ─── Export du calendrier (feature-calendar) ──────────────────────
// Deux sorties : .ics (agendas tiers) et PDF (enregistrable / imprimable).
// La période part d'une date de DÉPART modifiable — « le mois prochain » ou
// « les vacances de fevrier » sont des demandes courantes, qu'une periode
// figee sur aujourd'hui rendait impossibles. Mode « perso » = debut et fin
// libres.
// La GARDE suit la meme regle de priorite qu'a l'ecran (cf. gardePeriode) :
// exporter une garde differente de celle affichee serait pire que ne rien
// exporter du tout.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import {
  useUI, useT, useAuth, PageHeader, ChoixPuces, EmptyState, Bascule, SelecteurDate, confirmerSuppression,
  isoDeDate, decalerIso, frJMA,
} from '@mop/core';
import { useCalendrierActif } from './CalendrierActifContext';
import { CATEGORIES_EVT, TYPES_EVENEMENT, typeEvenement } from './data/categories';
import { ecouterEvenements } from './services/evenements';
import { genererICS, genererHTML, genererGrillesHTML, partager, exporterPDF, nomFichierSafe } from './services/export';
import { calculerGardePeriode } from './services/gardePeriode';
import { chargerVacancesConfig } from './services/vacances';
import { ecouterGrillesEDT, genererOccurrencesEDT, grilleActiveLe, creneauxDe } from './services/edt';
import { parserICS, importerEvenements } from './services/importIcs';

// `genererOccurrencesEDT` travaille sur des Date, pas des cles ISO.
const _dateDe = (dk) => { const [y, m, j] = String(dk).split('-').map(Number); return new Date(y, m - 1, j); };

const PERIODES = [
  { id: '1s', jours: 7,   cle: 'export.p1s' },
  { id: '2s', jours: 14,  cle: 'export.p2s' },
  { id: '1m', jours: 31,  cle: 'export.p1m' },
  { id: '3m', jours: 92,  cle: 'export.p3m' },
  { id: 'perso', jours: 0, cle: 'export.perso' },
];

export default function EcranExport({ onRetour }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user } = useAuth();
  const { calIdActif, calendriers } = useCalendrierActif();
  // Le calendrier EXPORTE n'est pas forcement l'actif : on peut vouloir sortir
  // le planning d'un autre sans changer de contexte dans toute l'app.
  const [calExport, setCalExport] = useState(calIdActif);
  useEffect(() => { setCalExport(prev => prev || calIdActif); }, [calIdActif]);
  const calId = calExport || calIdActif;
  const cal = (calendriers || []).find(c => c.id === calId) || null;

  const [periode, setPeriode] = useState('1m');
  const [depart, setDepart] = useState(() => isoDeDate(new Date()));
  const [finPerso, setFinPerso] = useState(() => decalerIso(isoDeDate(new Date()), 31));
  const [picker, setPicker] = useState(null);   // 'depart' | 'fin'
  const [avecGardes, setAvecGardes] = useState(true);
  const [gardes, setGardes] = useState([]);
  // Garde PAR JOUR + parents : la grille colore chaque case, le fil « Gardes »
  // n'a besoin que des segments — deux formes du meme calcul.
  const [gardeParJour, setGardeParJour] = useState({});
  const [parents, setParents] = useState(null);
  const [vacances, setVacances] = useState([]);
  const [avecEdt, setAvecEdt] = useState(true);
  // Paysage par defaut : le planning fait huit colonnes, illisibles en portrait.
  const [paysage, setPaysage] = useState(true);
  // Filtres : Set VIDE = aucun filtre = TOUT. Un filtre qui, par defaut,
  // n'exporterait rien serait un piege.
  const [cats, setCats] = useState(() => new Set());
  const [types, setTypes] = useState(() => new Set());
  const bascule = (setter) => (id) => setter(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const [grillesEdt, setGrillesEdt] = useState([]);
  const [map, setMap] = useState(null);       // null = en cours de lecture
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  // Une écoute refusée renvoyait une liste/un map VIDE en silence : l'écran
  // annonçait « rien à exporter » alors qu'il n'avait rien pu lire — même
  // correction que sur les contacts/le journal/la messagerie.
  const [erreurLecture, setErreurLecture] = useState('');

  const bornes = useMemo(() => {
    if (periode === 'perso') {
      // Bornes inversees : on les remet dans l'ordre plutot que de ne rien
      // exporter sans explication.
      return finPerso < depart ? { debut: finPerso, fin: depart } : { debut: depart, fin: finPerso };
    }
    const jours = (PERIODES.find(p => p.id === periode) || PERIODES[2]).jours;
    return { debut: depart, fin: decalerIso(depart, jours) };
  }, [periode, depart, finPerso]);

  useEffect(() => {
    setMap(null);
    setErreurLecture('');
    if (!calId) { setMap({}); return undefined; }
    return ecouterEvenements(calId, setMap, { min: bornes.debut, max: bornes.fin }, setErreurLecture);
  }, [calId, bornes.debut, bornes.fin]);

  // Garde de la periode : lecture PONCTUELLE (pas d'ecoute), meme regle de
  // priorite qu'a l'ecran.
  useEffect(() => {
    if (!calId || !avecGardes) { setGardes([]); setGardeParJour({}); setParents(null); return; }
    let vivant = true;
    calculerGardePeriode({ calId, debut: bornes.debut, fin: bornes.fin })
      .then(r => {
        if (!vivant) return;
        setGardes(r.segments || []);
        setGardeParJour(r.parJour || {});
        setParents(r.parents || null);
      })
      .catch(() => { if (vivant) { setGardes([]); setGardeParJour({}); setParents(null); } });
    return () => { vivant = false; };
  }, [calId, avecGardes, bornes.debut, bornes.fin]);

  // Le service travaille sur une LISTE plate : le map par jour est un détail de
  // stockage, pas un format d'échange.
  const evenements = useMemo(() => {
    const out = [];
    Object.entries(map || {}).forEach(([date, items]) => {
      (items || []).forEach(it => {
        if (cats.size && !cats.has(it.categorie || 'autre')) return;
        if (types.size && !types.has(typeEvenement(it))) return;
        out.push({ date, ...it });
      });
    });
    return out;
  }, [map, cats, types]);

  useEffect(() => {
    if (!calId) { setVacances([]); return; }
    let vivant = true;
    chargerVacancesConfig(calId)
      .then(cfg => { if (vivant) setVacances(Array.isArray(cfg?.periodes) ? cfg.periodes : []); })
      .catch(() => { if (vivant) setVacances([]); });
    return () => { vivant = false; };
  }, [calId]);

  useEffect(() => {
    if (!calId || !avecEdt) { setGrillesEdt([]); return undefined; }
    return ecouterGrillesEDT(calId, setGrillesEdt, setErreurLecture);
  }, [calId, avecEdt]);

  // Occurrences CALCULEES a la volee (jamais en base) — meme fonction que
  // l'affichage : une seule verite pour l'ecran et pour le PDF.
  const edtParJour = useMemo(() => {
    if (!avecEdt || !grillesEdt.length) return {};
    try {
      return genererOccurrencesEDT(grillesEdt, _dateDe(bornes.debut), _dateDe(bornes.fin), vacances);
    } catch { return {}; }
  }, [avecEdt, grillesEdt, bornes.debut, bornes.fin, vacances]);

  // Plage horaire de reference : celle de la grille EDT active sur la periode.
  // Sans elle, l'echelle se limite aux evenements — un debut de journee sans
  // cours n'apparaitrait pas.
  const plage = useMemo(() => {
    const g = grilleActiveLe(grillesEdt || [], bornes.debut) || (grillesEdt || [])[0];
    if (!g) return null;
    const slots = creneauxDe(g);
    if (!slots.length) return null;
    const min = (h) => { const [a, b] = String(h).split(':').map(Number); return a * 60 + (b || 0); };
    const pas = Math.max(15, min(slots[0].fin) - min(slots[0].debut));
    return { debut: slots[0].debut, fin: slots[slots.length - 1].fin, pas };
  }, [grillesEdt, bornes.debut]);

  const periodeLabel = `${frJMA(bornes.debut)} → ${frJMA(bornes.fin)}`;
  const nomCal = cal?.nom || t('calendrier.titre');

  async function exporterICS() {
    setBusy(true); setMsg('');
    try {
      const contenu = genererICS({ nomCalendrier: nomCal, calId, evenements, gardes });
      const r = await partager({
        nomFichier: nomFichierSafe(nomCal, 'ics'), contenu,
        mimeType: 'text/calendar;charset=utf-8', titre: nomCal,
      });
      setMsg(r.methode === 'download' ? t('export.telecharge') : t('export.partage'));
    } catch (e) { setMsg(e?.message || String(e)); }
    setBusy(false);
  }

  async function exporterRapport() {
    setBusy(true); setMsg('');
    try {
      // Le planning semaine par semaine est la piece maitresse du rapport :
      // il montre la garde ET les evenements, la ou la liste ne montre que les
      // evenements.
      const grilles = genererGrillesHTML({
        debut: bornes.debut, fin: bornes.fin,
        gardeParJour: avecGardes ? gardeParJour : {}, parents, evenements, vacances, edtParJour, plage,
      });
      const html = genererHTML({ nomCalendrier: nomCal, periodeLabel, evenements, gardes, grilles, paysage });
      const r = await exporterPDF({ html, titre: nomCal, paysage });
      // Repli si les modules PDF manquent : on partage au moins le document.
      if (!r.ok && r.motif === 'module') {
        await partager({ nomFichier: nomFichierSafe(nomCal, 'html'), contenu: html, mimeType: 'text/html', titre: nomCal });
        setMsg(t('export.rapportPret'));
      } else if (!r.ok) {
        setMsg(t('export.popupBloquee'));
      } else {
        setMsg(r.methode === 'impression' ? t('export.impressionOuverte') : t('export.pdfPret'));
      }
    } catch (e) { setMsg(e?.message || String(e)); }
    setBusy(false);
  }

  // Lecture du fichier : le web a l'input natif, le mobile passe par le
  // selecteur de documents. Aucun des deux ne peut lire l'autre.
  async function lireFichier() {
    if (Platform.OS === 'web') {
      return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.ics,text/calendar';
        input.onchange = async () => {
          const f = input.files && input.files[0];
          if (!f) return resolve(null);
          try { resolve(await f.text()); } catch { resolve(null); }
        };
        input.click();
      });
    }
    try {
      const DP = require('expo-document-picker');
      const r = await DP.getDocumentAsync({ type: ['text/calendar', 'application/octet-stream', '*/*'], copyToCacheDirectory: true });
      const uri = r?.assets?.[0]?.uri || (r?.type === 'success' ? r.uri : null);
      if (!uri) return null;
      const FS = require('expo-file-system');
      // expo-file-system : API « legacy » selon la version installee.
      if (FS.readAsStringAsync) return await FS.readAsStringAsync(uri);
      if (FS.File) return await new FS.File(uri).text();
      return null;
    } catch { return null; }
  }

  async function importerIcs() {
    setMsg('');
    const texte = await lireFichier();
    if (!texte) return;                       // annule par l'utilisateur
    let evts = [];
    try { evts = parserICS(texte); } catch { evts = []; }
    if (!evts.length) { setMsg(t('export.icsIllisible')); return; }
    // On ECRIT dans le calendrier : jamais sans confirmation, et en annoncant
    // combien et ou.
    confirmerSuppression(
      t('export.importTitre'),
      t('export.importMsg', { n: evts.length, nom: nomCal }),
      async () => {
        setBusy(true);
        const { ok, echecs } = await importerEvenements(calId, evts, { uid: user?.uid, nom: user?.prenom || user?.email || '' });
        setBusy(false);
        setMsg(echecs ? t('export.importPartiel', { n: ok, e: echecs }) : t('export.importOk', { n: ok }));
      },
    );
  }

  return (
    <View style={s.page}>
      <PageHeader titre={t('famille.t.exports')} iconeTitre="📤" onRetour={onRetour} />
      <ScrollView contentContainerStyle={s.corps}>
        <Text style={s.aide}>{t('export.aide')}</Text>

        {/* Calendrier a exporter : puces, comme le selecteur de plans. */}
        {(calendriers || []).length > 1 && (
          <>
            <Text style={s.groupe}>📅 {t('export.calendrier')}</Text>
            <ChoixPuces value={calId}
              options={(calendriers || []).map(c => ({ id: c.id, label: `${c.emoji || '📅'} ${c.nom}` }))}
              onChange={setCalExport} />
          </>
        )}
        {(calendriers || []).length <= 1 && <Text style={s.groupe}>📅 {nomCal}</Text>}
        <ChoixPuces value={periode} options={PERIODES.map(p => ({ id: p.id, label: t(p.cle) }))} onChange={setPeriode} />

        {/* Date de depart (ou bornes libres en mode perso). */}
        <View style={s.dates}>
          <TouchableOpacity style={s.dateBtn} onPress={() => setPicker('depart')}>
            <Text style={s.dateTxt}>📅 {periode === 'perso' ? t('export.du', { d: frJMA(bornes.debut) }) : t('export.depart', { d: frJMA(depart) })}</Text>
          </TouchableOpacity>
          {periode === 'perso' && (
            <TouchableOpacity style={s.dateBtn} onPress={() => setPicker('fin')}>
              <Text style={s.dateTxt}>🏁 {t('export.au', { d: frJMA(bornes.fin) })}</Text>
            </TouchableOpacity>
          )}
        </View>
        {periode !== 'perso' && <Text style={s.periode}>{periodeLabel}</Text>}

        {/* Gardes : proposees seulement si le calendrier en a. */}
        <View style={s.ligneOpt}>
          <Text style={s.optTxt}>🔄 {t('export.inclureGardes')}</Text>
          <Bascule valeur={avecGardes} onChange={setAvecGardes} accessibilityLabel={t('export.inclureGardes')} />
        </View>
        {avecGardes && gardes.length > 0 && (
          <Text style={s.compte}>{t('export.nSegmentsGarde', { n: gardes.length })}</Text>
        )}
        <View style={s.ligneOpt}>
          <Text style={s.optTxt}>🎓 {t('export.inclureEdt')}</Text>
          <Bascule valeur={avecEdt} onChange={setAvecEdt} accessibilityLabel={t('export.inclureEdt')} />
        </View>
        <View style={s.ligneOpt}>
          <Text style={s.optTxt}>📄 {t('export.paysage')}</Text>
          <Bascule valeur={paysage} onChange={setPaysage} accessibilityLabel={t('export.paysage')} />
        </View>

        {!!erreurLecture && (
          <View style={s.bandeau}>
            <Text style={s.bandeauTxt}>⚠️ {t('calendrier.erreurLectureEvenements')}</Text>
            <Text style={s.bandeauDet}>{erreurLecture}</Text>
          </View>
        )}
        {map === null
          ? <ActivityIndicator color={UI.accent} style={{ marginTop: 20 }} />
          : <Text style={s.compte}>{t('export.nEvenements', { n: evenements.length })}</Text>}

        {/* Categories et types : multi-selection, rien de coche = tout. */}
        <Text style={s.groupe}>{t('export.filtres')}</Text>
        <Text style={s.aideMini}>{t('export.filtresAide')}</Text>
        <View style={s.puces}>
          {CATEGORIES_EVT.map(c => (
            <TouchableOpacity key={c.id} style={[s.puce, cats.has(c.id) && s.puceOn]}
              onPress={() => bascule(setCats)(c.id)}>
              <Text style={[s.puceTxt, cats.has(c.id) && s.puceTxtOn]}>{c.emoji} {t(c.i18n)}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <View style={s.puces}>
          {TYPES_EVENEMENT.map(x => (
            <TouchableOpacity key={x.id} style={[s.puce, types.has(x.id) && s.puceOn]}
              onPress={() => bascule(setTypes)(x.id)}>
              <Text style={[s.puceTxt, types.has(x.id) && s.puceTxtOn]}>{x.emoji} {t(x.i18n)}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {!!msg && <Text style={s.msg}>{msg}</Text>}

        {/* Actions : pas d'export possible sans rien à exporter — on le dit au
            lieu de produire un fichier vide. */}
        {!erreurLecture && map !== null && evenements.length === 0 && (
          <EmptyState icon="📭" texte={t('export.rien')} aide={t('export.rienAide')} />
        )}
        {evenements.length > 0 && (
          <>
            <TouchableOpacity style={s.action} activeOpacity={0.7} disabled={busy} onPress={exporterICS}>
              <Text style={s.actionEmoji}>🗓️</Text>
              <View style={{ flex: 1 }}>
                <Text style={s.actionTitre}>{t('export.ics')}</Text>
                <Text style={s.actionSous}>{t('export.icsSous')}</Text>
              </View>
              <Text style={s.chev}>›</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.action} activeOpacity={0.7} disabled={busy} onPress={exporterRapport}>
              <Text style={s.actionEmoji}>📄</Text>
              <View style={{ flex: 1 }}>
                <Text style={s.actionTitre}>{t('export.pdf')}</Text>
                <Text style={s.actionSous}>{t('export.pdfSous')}</Text>
              </View>
              <Text style={s.chev}>›</Text>
            </TouchableOpacity>
          </>
        )}
        {/* IMPORT : seule fonction d'ENTREE. Separee visuellement des exports,
            car elle ECRIT dans le calendrier au lieu d'en sortir un fichier. */}
        <Text style={s.groupe}>{t('export.importGroupe')}</Text>
        <TouchableOpacity style={s.action} activeOpacity={0.7} disabled={busy || !calId} onPress={importerIcs}>
          <Text style={s.actionEmoji}>📥</Text>
          <View style={{ flex: 1 }}>
            <Text style={s.actionTitre}>{t('export.importer')}</Text>
            <Text style={s.actionSous}>{t('export.importerSous')}</Text>
          </View>
          <Text style={s.chev}>›</Text>
        </TouchableOpacity>

        {busy && <ActivityIndicator color={UI.accent} style={{ marginTop: 14 }} />}
      </ScrollView>

      <SelecteurDate visible={!!picker} valeur={picker === 'fin' ? finPerso : depart}
        onFermer={() => setPicker(null)}
        onChange={(dk) => { if (picker === 'fin') setFinPerso(dk); else setDepart(dk); setPicker(null); }} />
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  page:   { flex: 1, backgroundColor: UI.bg },
  corps:  { padding: 14, paddingBottom: 40 },
  aide:   { fontSize: 12, color: UI.muted, lineHeight: 17, marginBottom: 14 },
  groupe: { fontSize: 12, fontWeight: '800', color: UI.muted, letterSpacing: 0.4, marginBottom: 8 },
  periode:{ fontSize: 12.5, color: UI.muted, marginTop: 8 },
  compte: { fontSize: 13, color: UI.ink, fontWeight: '700', marginTop: 4, marginBottom: 12 },
  msg:    { fontSize: 12.5, color: UI.accent, marginBottom: 10 },
  dates:  { flexDirection: 'row', gap: 8, marginTop: 10 },
  dateBtn:{ flex: 1, backgroundColor: UI.champ, borderRadius: 10, borderWidth: 1, borderColor: UI.border,
            paddingVertical: 10, paddingHorizontal: 12 },
  dateTxt:{ fontSize: 13, color: UI.ink, fontWeight: '700' },
  ligneOpt:{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
             marginTop: 12, marginBottom: 4 },
  optTxt: { fontSize: 13.5, color: UI.ink, fontWeight: '700' },
  aideMini: { fontSize: 11.5, color: UI.faint, marginBottom: 6 },
  puces:  { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 8 },
  puce:   { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, borderWidth: 1,
            borderColor: UI.border, backgroundColor: UI.champ },
  puceOn: { borderColor: UI.accent, backgroundColor: UI.accentTint },
  puceTxt:{ fontSize: 12, color: UI.muted, fontWeight: '700' },
  puceTxtOn: { color: UI.accent },
  action: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card,
            borderRadius: 13, borderWidth: 0.5, borderColor: UI.border, padding: 13, marginBottom: 9 },
  actionEmoji: { fontSize: 22 },
  actionTitre: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  actionSous:  { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  chev:   { fontSize: 18, color: UI.accent, fontWeight: '800' },
  bandeau:    { backgroundColor: UI.dangerTint || '#fdecec', borderRadius: 12, borderWidth: 0.5, borderColor: UI.danger || '#e5484d', padding: 11, marginBottom: 10 },
  bandeauTxt: { fontSize: 13, fontWeight: '800', color: UI.danger || '#e5484d' },
  bandeauDet: { fontSize: 11.5, color: UI.muted, marginTop: 3 },
});
